import { useNavigate } from 'react-router';
import type { ApiError } from '@/lib/api/client';
import { signOut } from '@/lib/supabase/auth';
import { AuthLayout } from './AuthLayout';

/**
 * Two rejections that look similar and are not.
 *
 * `wrong_domain` — a personal account. The session must be SIGNED OUT before this
 * screen is useful, otherwise Google silently re-selects the same rejected account and
 * the user is stuck in a loop with no way to pick a different one.
 *
 * `no_employee_record` — a real colleague whose row does not exist yet. Their session
 * is fine and is deliberately kept: `useCurrentUser` polls, so the screen resolves
 * itself the moment an admin adds them.
 */
export function AccessDeniedScreen({ error }: { error: ApiError }) {
  const navigate = useNavigate();
  const reason = error.reason;

  if (reason === 'wrong_domain') {
    return (
      <Card
        title="That account can’t be used here"
        body="Tap Academy is limited to company accounts. Sign out and choose your work address."
        action={{
          label: 'Sign out and try another account',
          async onClick() {
            await signOut();
            void navigate('/login', { replace: true });
          },
        }}
      />
    );
  }

  if (reason === 'no_employee_record' || reason === 'suspended') {
    return (
      <Card
        title={reason === 'suspended' ? 'Your access has been paused' : 'You’re not on the list yet'}
        body={
          reason === 'suspended'
            ? 'An administrator has paused this account. Ask your manager if this is unexpected.'
            : 'An administrator needs to add you before you can see your board. This page will update on its own once they do.'
        }
        action={{
          label: 'Sign out',
          async onClick() {
            await signOut();
            void navigate('/login', { replace: true });
          },
        }}
      />
    );
  }

  return (
    <Card
      title="Something went wrong"
      body={error.message}
      footnote={error.requestId ? `Reference: ${error.requestId}` : undefined}
      action={{ label: 'Reload', onClick: () => window.location.reload() }}
    />
  );
}

function Card(props: {
  title: string;
  body: string;
  footnote?: string | undefined;
  action: { label: string; onClick: () => void | Promise<void> };
}) {
  return (
    <AuthLayout>
      <h1 className="text-base font-semibold tracking-tight">{props.title}</h1>
      <p className="mt-2 text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>
        {props.body}
      </p>
      <button
        type="button"
        onClick={() => void props.action.onClick()}
        className="mt-6 min-h-11 w-full rounded-xl border px-4 text-sm font-medium"
        style={{ borderColor: 'var(--border)', background: 'var(--surface-raised)' }}
      >
        {props.action.label}
      </button>
      {props.footnote && (
        <p className="mt-4 font-mono text-[11px]" style={{ color: 'var(--text-faint)' }}>
          {props.footnote}
        </p>
      )}
    </AuthLayout>
  );
}
